"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface Question {
  id: string;
  module: string;
  question: string;
  options: string[];
  microFeedback?: string;
}

interface DiagnosticResponse {
  questionId: string;
  answer: string;
}

interface ProgressState {
  index: number;
  responses: DiagnosticResponse[];
  contactName: string;
  contactEmail: string;
  startedAt: string;
}

const STORAGE_KEY = "leadership_diagnostic_progress";

const questions: Question[] = [
  {
    id: "q1",
    module: "Self-Leadership Under Pressure",
    question: "When pressure increases, my default response is:",
    options: [
      "Narrow focus and push harder",
      "Pause and analyze longer",
      "Take direct control",
      "Function outwardly while feeling internal friction",
    ],
    microFeedback: "Pressure reveals operating systems, not character.",
  },
  {
    id: "q2",
    module: "Self-Leadership Under Pressure",
    question:
      "I notice inconsistency between my expectations of others and my own behavior:",
    options: ["Often", "Sometimes", "Rarely", "Almost never"],
  },
  {
    id: "q3",
    module: "Self-Leadership Under Pressure",
    question: "Under uncertainty, I rely most on:",
    options: [
      "Data and structure",
      "Pattern recognition",
      "Past experience",
      "External validation",
    ],
  },
  {
    id: "q4",
    module: "Decision-Making",
    question: "I delay decisions primarily because:",
    options: [
      "Information feels incomplete",
      "Consequences feel complex",
      "I want to avoid irreversible outcomes",
      "I usually decide quickly",
    ],
  },
  {
    id: "q5",
    module: "Decision-Making",
    question: "I step in personally when:",
    options: [
      "Standards drop",
      "Speed matters",
      "Control feels unstable",
      "I generally don't",
    ],
  },
  {
    id: "q6",
    module: "Decision-Making",
    question: "Delegating ownership feels:",
    options: [
      "Necessary but uncomfortable",
      "Natural",
      "Risky",
      "Context-dependent",
    ],
  },
  {
    id: "q7",
    module: "Leadership Identity",
    question: "My leadership role feels:",
    options: [
      "Clear and embodied",
      "Clear but draining",
      "Functional but misaligned",
      "Unclear",
    ],
  },
  {
    id: "q8",
    module: "Leadership Identity",
    question: "I feel responsible for:",
    options: [
      "Outcomes",
      "People's emotional states",
      "Systems and structure",
      "All of the above",
    ],
  },
  {
    id: "q9",
    module: "Leadership Identity",
    question:
      "The gap between how I'm perceived and how I feel internally is:",
    options: ["Large", "Moderate", "Small", "Nonexistent"],
  },
  {
    id: "q10",
    module: "Systemic Blind Spots",
    question: "Problems I face tend to:",
    options: [
      "Repeat in different forms",
      "Be situational",
      "Be externally driven",
      "Rarely repeat",
    ],
  },
  {
    id: "q11",
    module: "Systemic Blind Spots",
    question: "Feedback I receive is usually:",
    options: ["Filtered", "Direct", "Incomplete", "Avoided"],
  },
  {
    id: "q12",
    module: "Systemic Blind Spots",
    question: "When something breaks, my first instinct is to ask:",
    options: [
      "What failed in the system",
      "Who is responsible",
      "What I missed",
      "How fast it can be fixed",
    ],
  },
  {
    id: "q13",
    module: "Integration",
    question: "My current operating capacity is:",
    options: [
      "Above sustainable levels",
      "At capacity",
      "Below capacity",
      "In recovery",
    ],
  },
  {
    id: "q14",
    module: "Integration",
    question: "What I need most right now is:",
    options: ["Clarity", "Alignment", "Structure", "Reduced internal noise"],
  },
  {
    id: "q15",
    module: "Integration",
    question:
      "I am willing to see uncomfortable patterns if they are useful:",
    options: ["Yes", "Mostly", "Unsure", "No"],
  },
];

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function DiagnosticFlow() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState<DiagnosticResponse[]>([]);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [startedAt, setStartedAt] = useState<string>(() => new Date().toISOString());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [savedProgress, setSavedProgress] = useState<ProgressState | null>(null);
  const [showContactStep, setShowContactStep] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved) as Partial<ProgressState>;
      if ((parsed.responses?.length ?? 0) > 0) {
        setSavedProgress({
          index: parsed.index ?? 0,
          responses: parsed.responses ?? [],
          contactName: parsed.contactName ?? "",
          contactEmail: parsed.contactEmail ?? "",
          startedAt: parsed.startedAt ?? new Date().toISOString(),
        });
        setShowRestorePrompt(true);
      }
    } catch {
      // ignore local storage errors
    }
  }, []);

  const saveProgress = useCallback(
    (index: number, nextResponses: DiagnosticResponse[], nextName: string, nextEmail: string, nextStartedAt: string) => {
      try {
        const payload: ProgressState = {
          index,
          responses: nextResponses,
          contactName: nextName,
          contactEmail: nextEmail,
          startedAt: nextStartedAt,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch {
        // ignore local storage errors
      }
    },
    [],
  );

  const persistCurrentProgress = useCallback(
    (nextName = contactName, nextEmail = contactEmail) => {
      saveProgress(currentIndex, responses, nextName, nextEmail, startedAt);
    },
    [contactEmail, contactName, currentIndex, responses, saveProgress, startedAt],
  );

  const clearProgress = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore local storage errors
    }
  }, []);

  const handleRestoreProgress = () => {
    if (!savedProgress) return;
    setCurrentIndex(savedProgress.index);
    setResponses(savedProgress.responses);
    setContactName(savedProgress.contactName);
    setContactEmail(savedProgress.contactEmail);
    setStartedAt(savedProgress.startedAt);
    setShowRestorePrompt(false);
  };

  const handleStartFresh = () => {
    clearProgress();
    setShowRestorePrompt(false);
    setSavedProgress(null);
    setCurrentIndex(0);
    setResponses([]);
    setContactName("");
    setContactEmail("");
    setShowContactStep(false);
    setStartedAt(new Date().toISOString());
  };

  const currentQuestion = questions[currentIndex];
  const progress = useMemo(() => {
    if (showContactStep) return 100;
    return ((currentIndex + 1) / questions.length) * 100;
  }, [currentIndex, showContactStep]);

  const handleAnswer = (answer: string) => {
    const nextResponses = [...responses, { questionId: currentQuestion.id, answer }];
    setResponses(nextResponses);
    saveProgress(currentIndex + 1, nextResponses, contactName, contactEmail, startedAt);

    if (currentQuestion.microFeedback) {
      setShowFeedback(true);
      setTimeout(() => {
        setShowFeedback(false);
        proceedToNext(nextResponses);
      }, 2200);
      return;
    }

    proceedToNext(nextResponses);
  };

  const proceedToNext = (nextResponses: DiagnosticResponse[]) => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      return;
    }

    setShowContactStep(true);
  };

  const handleSubmit = async () => {
    setContactError(null);
    setSubmitError(null);

    const trimmedName = contactName.trim();
    const trimmedEmail = contactEmail.trim();

    if (trimmedName.length < 2) {
      setContactError("Please enter your full name.");
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setContactError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);

    try {
      const completionTimeSeconds = Math.max(
        0,
        Math.round((Date.now() - new Date(startedAt).getTime()) / 1000),
      );

      const response = await fetch("/api/diagnostic", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contactName: trimmedName,
          contactEmail: trimmedEmail,
          completionTimeSeconds,
          responses,
        }),
      });

      const result = (await response.json()) as { error?: string };

      if (!response.ok) {
        throw new Error(result.error || "There was an issue submitting your diagnostic.");
      }

      clearProgress();
      setIsSubmitted(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : "There was an issue submitting your diagnostic.";
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (showContactStep) {
      setShowContactStep(false);
      return;
    }

    if (currentIndex <= 0) return;

    const newIndex = currentIndex - 1;
    const newResponses = responses.slice(0, -1);
    setCurrentIndex(newIndex);
    setResponses(newResponses);
    saveProgress(newIndex, newResponses, contactName, contactEmail, startedAt);
  };

  if (showRestorePrompt) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-lg mx-auto text-center space-y-8 animate-fadeIn">
          <div className="size-16 rounded-full bg-accent/10 flex items-center justify-center text-accent mx-auto">
            <Icon name="BookmarkIcon" size={28} />
          </div>
          <div className="space-y-3">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              Resume where you left off?
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              You have saved progress from a previous session. We can restore your diagnostic instantly.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleRestoreProgress}
              className="px-8 py-3 bg-accent text-accent-foreground rounded-full text-sm font-bold uppercase tracking-widest hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Continue
            </button>
            <button
              onClick={handleStartFresh}
              className="px-8 py-3 bg-card border border-border text-foreground rounded-full text-sm font-bold uppercase tracking-widest hover:bg-secondary transition-all focus:outline-none focus:ring-2 focus:ring-ring"
            >
              Start Fresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitting) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-lg mx-auto text-center space-y-8 animate-fadeIn">
          <div className="size-16 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto" />
          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-foreground">Submitting your diagnostic...</h2>
            <p className="text-base text-muted-foreground">
              Your responses and contact details are being saved securely.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (submitError) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-lg mx-auto text-center space-y-8 animate-fadeIn">
          <div className="size-16 rounded-full bg-destructive/10 flex items-center justify-center text-destructive mx-auto">
            <Icon name="ExclamationCircleIcon" size={28} />
          </div>
          <div className="space-y-3">
            <h2 className="text-2xl font-bold text-foreground">Submission failed</h2>
            <p className="text-base text-muted-foreground leading-relaxed">{submitError}</p>
          </div>
          <button
            onClick={() => setSubmitError(null)}
            className="px-8 py-3 bg-accent text-accent-foreground rounded-full text-sm font-bold uppercase tracking-widest hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-ring"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-2xl mx-auto text-center space-y-8 animate-fadeIn">
          <div className="size-20 rounded-full bg-success/10 flex items-center justify-center text-success mx-auto">
            <Icon name="CheckCircleIcon" size={32} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
            Thank you for completing the Leadership Diagnostic.
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Your responses were saved successfully. Horváth Anett can now review them personally.
          </p>
          <div className="glass-card p-8 text-left space-y-4">
            <p className="text-base text-foreground leading-relaxed">
              You will be contacted at <span className="font-semibold">{contactEmail}</span> if a follow-up conversation is the right next step.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              No charts. No scores. No automated interpretation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showFeedback && currentQuestion.microFeedback) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6">
        <div className="max-w-2xl mx-auto text-center animate-fadeIn">
          <div className="glass-card p-12 border-l-4 border-accent">
            <p className="text-2xl md:text-3xl text-foreground leading-relaxed italic">
              {currentQuestion.microFeedback}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showContactStep) {
    return (
      <div className="min-h-[80vh] flex flex-col px-6 py-10">
        <div className="w-full h-1 bg-border mb-10">
          <div className="h-full bg-accent transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <div className="max-w-2xl mx-auto w-full space-y-8 animate-fadeIn">
          <div className="space-y-3 text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Final step</p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">Where should the follow-up reach you?</h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              Your diagnostic is complete. Add your details so the submission can be reviewed manually.
            </p>
          </div>

          <div className="glass-card p-8 space-y-5">
            <div className="space-y-2">
              <label htmlFor="contact-name" className="text-sm font-semibold text-foreground">
                Full name
              </label>
              <input
                id="contact-name"
                type="text"
                autoComplete="name"
                value={contactName}
                onChange={(event) => {
                  setContactName(event.target.value);
                  persistCurrentProgress(event.target.value, contactEmail);
                }}
                className="w-full px-4 py-3 bg-background border border-border rounded-2xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Your full name"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="contact-email" className="text-sm font-semibold text-foreground">
                Email address
              </label>
              <input
                id="contact-email"
                type="email"
                autoComplete="email"
                value={contactEmail}
                onChange={(event) => {
                  setContactEmail(event.target.value);
                  persistCurrentProgress(contactName, event.target.value);
                }}
                className="w-full px-4 py-3 bg-background border border-border rounded-2xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="you@example.com"
              />
            </div>
            {contactError && (
              <p className="text-sm text-destructive" role="alert">
                {contactError}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={handlePrevious}
                className="px-6 py-3 bg-card border border-border text-foreground rounded-full text-sm font-bold uppercase tracking-widest hover:bg-secondary transition-all"
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-6 py-3 bg-accent text-accent-foreground rounded-full text-sm font-bold uppercase tracking-widest hover:scale-[1.01] transition-transform"
              >
                Submit Diagnostic
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex flex-col">
      <div className="w-full h-1 bg-border" role="progressbar" aria-valuenow={currentIndex + 1} aria-valuemin={1} aria-valuemax={questions.length}>
        <div className="h-full bg-accent transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-3xl mx-auto w-full space-y-10 animate-fadeIn">
          <div className="text-center space-y-4">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              {currentQuestion.module}
            </p>
            <h1 className="text-3xl md:text-5xl font-bold text-foreground leading-tight">
              {currentQuestion.question}
            </h1>
            <p className="text-sm text-muted-foreground">
              Question {currentIndex + 1} of {questions.length}
            </p>
          </div>

          <div className="grid gap-4">
            {currentQuestion.options.map((option) => (
              <button
                key={option}
                onClick={() => handleAnswer(option)}
                className="w-full text-left glass-card p-6 rounded-3xl hover:border-accent/50 hover:bg-accent/5 transition-all focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <span className="text-base md:text-lg text-foreground leading-relaxed">{option}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="ArrowLeftIcon" size={16} />
              Previous
            </button>
            <p className="text-xs text-muted-foreground">Progress is saved in this browser.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
